/**
 * API path constants for the tenant admin application.
 */
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    CHANGE_PASSWORD: '/auth/change-password',
  },

  USERS: {
    ME: '/users/me',
    CHANGE_PASSWORD: '/users/me/change-password',
    BY_ID: (id: string) => `/users/${id}`,
  },

  MANAGE_USERS: {
    LIST: '/manage-users',
    CREATE: '/manage-users',
    GET_BY_ID: (id: string) => `/manage-users/${id}`,
    UPDATE: (id: string) => `/manage-users/${id}`,
  },

  BATCH: {
    LIST: '/batch',
    STATS: '/batch/stats',
    PRE_VALIDATE: '/batch/pre-validate',
    UPLOAD: '/batch/upload',
    MODELS: '/batch/models',
    EXISTS: '/batch/exists',
    GET_BY_ID: (id: string) => `/batch/${id}`,
    RECONCILIATION: (id: string) => `/batch/${id}/reconciliation`,
    RISK_CASES: (id: string) => `/batch/${id}/risk-cases`,
    ASSIGN_RISK_CASE: (batchId: string, caseId: string) => `/batch/${batchId}/risk-cases/${caseId}/assign`,
    RISK_CASE_DETAIL: (batchId: string, caseId: string) => `/batch/${batchId}/risk-cases/${caseId}/detail`,
    ANALYSIS: '/batch/analysis',
    COMPARE: '/batch/compare',
    REPORT: (id: string) => `/batch/${id}/report`,
  },

  INVOICES: {
    LIST: '/workspace/billing/invoices',
    SUMMARY: '/workspace/billing/invoices/summary',
    LIST_BY_ID: (invoiceId: string) =>
      `/workspace/billing/invoices/${invoiceId}/detailsByInvoiceId`,
    GENERATE_DATA: (invoiceNo: string) =>
      `/workspace/billing/invoices/${invoiceNo}/generate-data`,
    DOWNLOAD: (invoiceNo: string) =>
      `/workspace/billing/invoices/${invoiceNo}/download`,
    PAYMENT_HISTORY: (invoiceNo: string) =>
      `/workspace/billing/invoices/${invoiceNo}/payments`,
  },

  BILLING: {
    CATALOG: '/workspace/billing/catalog',
    SUBSCRIPTION: '/workspace/billing/subscription',
    SYNC_PAYMENT: '/workspace/billing/sync-payment',
    PAYMENT: '/workspace/billing/payment',
    ADDON_PLANS: '/workspace/billing/addon-catalog',
    ADDON_PAYMENT: '/workspace/billing/addon-payment',
    USAGE: '/workspace/billing/usage',
    MODELS: '/workspace/billing/models',
    PREPAID_MODEL_SCORES: '/workspace/billing/recordcountForPrepaid',
  },

  AUDIT_TRAIL: {
    LIST: '/workspace/audit-trail/search',
    SUMMARY: '/workspace/audit-trail/summary',
    SUMMARY_EXPORT: (logId: string) => `/workspace/audit-trail/${logId}/export`,
    FILTERS: '/workspace/audit-trail/filter-options',
    EXPORT: '/workspace/audit-trail/export',
  },

  MAKER: {
    MY_QUEUE: '/batch/my-queue',
    CHECKERS: '/batch/checkers',
    SUBMIT_DECISION: (batchId: string, caseId: string) =>
      `/batch/${batchId}/risk-cases/${caseId}/decision`,
  },

  CHECKER: {
    MY_QUEUE: '/batch/checker-queue',
    SUBMIT_REVIEW: (batchId: string, caseId: string) =>
      `/batch/${batchId}/risk-cases/${caseId}/review`,
  },

  DASHBOARD: {
    REVENUE_SUMMARY: (year: string) => `/workspace/billing/aggregate/revenue?year=${year}`,
    INVOICE_RATIO_SUMMARY:(year: string) => `/workspace/billing/aggregate/invoice-ratio?year=${year}`,
    BUSINESS_SUMMARY: (year: string) => `/workspace/billing/aggregate/active-inactive?year=${year}`,
    SERVICE_USED_SUMMARY: (year: string) => `/workspace/billing/aggregate/payment-types?year=${year}`,
    KPI_SUMMARY: '/workspace/risk-monitoring/kpis',
    TRIGGER_TREND_SUMMARY: '/workspace/risk-monitoring/trends',
    RISK_DISTRIBUTION_SUMMARY: '/workspace/risk-monitoring/risk-distribution',
    TRIGGER_STATUS_SUMMARY: '/workspace/risk-monitoring/trigger-status',
    BRANCHCOUNT_SUMMARY: '/workspace/risk-monitoring/branches',
    PRODUCT_COUNT_SUMMARY: '/workspace/risk-monitoring/products',
    SEARCH_BRANCHES: '/workspace/risk-monitoring/search/branches',
    SEARCH_PRODUCTS: '/workspace/risk-monitoring/search/products',
    TRIGGER_RULES_SUMMARY: '/workspace/risk-monitoring/triggering-rules',
    HIGH_RISK_LOAN_SEGEMENT_SUMMARY: '/workspace/risk-monitoring/loan-segments/high-risk',
    MID_RISK_LOAN_SEGEMENT_SUMMARY: '/workspace/risk-monitoring/loan-segments/mid-risk',
    EXPOSURE_SUMMARY_LOAN_SEGEMENT_SUMMARY: '/workspace/risk-monitoring/loan-segments/exposure',
    EXPORT: `/workspace/risk-monitoring/export`,
  },

  BRANCH_ANALYTICS: {
    CARDS_SUMMARY: '/workspace/analytics/branch/kpis',
    TRIGGER_TREND_SUMMARY: '/workspace/analytics/branch/trends',
    RISK_DISTRIBUTION_SUMMARY: '/workspace/analytics/branch/risk-distribution',
    PRODUCT_CONTRIBUTIONS_SUMMARY: '/workspace/analytics/branch/products',
    MONTHLY_EXPOSURE_SUMMARY: '/workspace/analytics/branch/exposure-trend',
    LOAN_AMOUNT_DISTRIBUTION_SUMMARY: '/workspace/analytics/branch/loan-amount-distribution',
    TOP_RULES_SUMMARY: '/workspace/analytics/branch/top-rules',
    BRANCH_SUMMARY: '/workspace/analytics/branch/summary',
    CUSTOMER_DRILL_DOWN_SUMMARY: '/workspace/analytics/branch/customers',
    EXPORT: '/workspace/analytics/branch/export',
  },

  MONTHLY_TRIGGER_TREND: {
    KPIS: '/workspace/analytics/monthly-trigger-trend/kpis',
    TRIGGER_TREND: '/workspace/analytics/monthly-trigger-trend/trigger-trend',
    EXPOSURE_TREND: '/workspace/analytics/monthly-trigger-trend/exposure-trend',
    TRIGGER_RATE_TREND: '/workspace/analytics/monthly-trigger-trend/trigger-rate-trend',
    RISK_MIX: '/workspace/analytics/monthly-trigger-trend/risk-mix',
    HEAT_MAP: '/workspace/analytics/monthly-trigger-trend/heat-map',
    MONTH_SUMMARY: '/workspace/analytics/monthly-trigger-trend/month-summary',
    COMPARE_BRANCHES: '/workspace/analytics/monthly-trigger-trend/compare/branches',
    COMPARE_PRODUCTS: '/workspace/analytics/monthly-trigger-trend/compare/products',
    EXPORT: '/workspace/analytics/monthly-trigger-trend/export',
  },

  RISK_DISTRIBUTION: {
    KPIS: '/workspace/analytics/risk-distribution/kpis',
    RISK_SPLIT: '/workspace/analytics/risk-distribution/risk-split',
    BRANCH_SPLIT: '/workspace/analytics/risk-distribution/branch-split',
    PRODUCT_SPLIT: '/workspace/analytics/risk-distribution/product-split',
    LOAN_BUCKETS: '/workspace/analytics/risk-distribution/loan-buckets',
    BRANCH_RANKING: '/workspace/analytics/risk-distribution/branch-ranking',
    PRODUCT_RANKING: '/workspace/analytics/risk-distribution/product-ranking',
    CUSTOMERS: '/workspace/analytics/risk-distribution/customers',
    EXPORT: '/workspace/analytics/risk-distribution/export',
  },

  TRIGGER_MOVEMENT: {
    KPIS: '/workspace/analytics/trigger-movement/kpis',
    MOVEMENT_FLOW: '/workspace/analytics/trigger-movement/movement-flow',
    MOVEMENT_FUNNEL: '/workspace/analytics/trigger-movement/movement-funnel',
    MOVEMENT_TREND: '/workspace/analytics/trigger-movement/movement-trend',
    CUSTOMERS: '/workspace/analytics/trigger-movement/customers',
    CLOSURE_REASONS: '/workspace/analytics/trigger-movement/closure-reasons',
    ESCALATION_REASONS: '/workspace/analytics/trigger-movement/escalation-reasons',
    EXPORT: '/workspace/analytics/trigger-movement/export',
  },

  PRODUCT_ANALYTICS: {
    KPIS: '/workspace/analytics/product/kpis',
    MONTHLY_TREND: '/workspace/analytics/product/monthly-trend',
    BRANCHES: '/workspace/analytics/product/branches',
    RULE_DISTRIBUTION: '/workspace/analytics/product/rule-distribution',
    EXPOSURE_DISTRIBUTION: '/workspace/analytics/product/exposure-distribution',
    LOAN_BUCKETS: '/workspace/analytics/product/loan-buckets',
    SUMMARY: '/workspace/analytics/product/summary',
    BRANCH_PERFORMANCE: '/workspace/analytics/product/branch-performance',
    CUSTOMERS: '/workspace/analytics/product/customers',
    EXPORT: '/workspace/analytics/product/export',
  },

  RULE_ANALYTICS: {
    KPIS: '/workspace/analytics/rule/kpis',
    MONTHLY_TREND: '/workspace/analytics/rule/monthly-trend',
    BRANCHES: '/workspace/analytics/rule/branches',
    PRODUCTS: '/workspace/analytics/rule/products',
    RISK_DISTRIBUTION: '/workspace/analytics/rule/risk-distribution',
    TOP_RULES_BY_CUSTOMERS: '/workspace/analytics/rule/top-rules-by-customers',
    OVERRIDE_RATES: '/workspace/analytics/rule/override-rates',
    CONFIGURATION: '/workspace/analytics/rule/configuration',
    PERFORMANCE: '/workspace/analytics/rule/performance',
    CUSTOMERS: '/workspace/analytics/rule/customers',
    EXPORT: '/workspace/analytics/rule/export',
  },

  LOAN_SEGMENT_ANALYSIS: {
    CARDS_SUMMARY: '/workspace/analytics/loan-segment/kpis',
    LOAN_BUCKET_DISTRIBUTION: '/workspace/analytics/loan-segment/loan-bucket-chart',
    EXPOSURE_MIX: '/workspace/analytics/loan-segment/exposure-mix',
    BRANCHES: '/workspace/analytics/loan-segment/branches',
    PRODUCTS: '/workspace/analytics/loan-segment/products',
    BUCKET_SUMMARY: '/workspace/analytics/loan-segment/bucket-summary',
    CUSTOMERS: '/workspace/analytics/loan-segment/customers',
    HIGH_RISK_LOAN_SEGEMENT_EXPORT: '/workspace/risk-monitoring/loan-segments/high-risk/export',
    MID_RISK_LOAN_SEGEMENT_EXPORT: '/workspace/risk-monitoring/loan-segments/mid-risk/export',
    EXPOSURE_SUMMARY_LOAN_SEGEMENT_EXPORT: '/workspace/risk-monitoring/loan-segments/exposure/export',
  }
} as const;
